import { useMemo, useState } from 'react';
import { api } from '../api.js';
import {
  daysFromNow,
  groupReadings,
  assessFreshnessClient,
  currentReadingsClient,
  daysSinceReading,
} from '../utils.js';
import { useConfig } from '../config.js';
import { effectiveParam } from '../params.js';
import { formatSalinity, readSalinityUnit, salinityUnitLabel } from '../salinity.js';
import { matchTaskExplainer, explainerForTank } from '../../../shared/task-explainers.js';
import { isFreshwaterTank } from '../first-run.js';
import { isTaskLapsed, lastDoneLabel } from '../../../shared/task-schedule.js';
import { WaterChangeSheet } from './WaterChangeSheet.jsx';
import { useConfirm } from './ConfirmDialog.jsx';

const ATO_CADENCES = [3, 5, 7, 14];
const atoOfferKey = (tankId) => `aqs.ato_offer_dismissed.${tankId}`;

export function TasksView({ tank, onReload, onOpenLog, onOpenIcp }) {
  const config = useConfig();
  const confirm = useConfirm();
  const explainers = config?.tasks?.explainers || null;
  const [completing, setCompleting] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [waterChange, setWaterChange] = useState(false);
  const [actionErr, setActionErr] = useState('');
  // A viewer's taps only ever answered 403, and with nothing on screen.
  const canEdit = tank.role !== 'viewer';
  const byParam = useMemo(() => groupReadings(tank.readings), [tank.readings]);

  const groups = useMemo(() => {
    const g = { lapsed: [], overdue: [], dueToday: [], soon: [], later: [] };
    for (const t of tank.tasks || []) {
      if (isTaskLapsed(t)) {
        g.lapsed.push(t);
        continue;
      }
      const d = daysFromNow(t.due);
      if (d == null) g.later.push(t);
      else if (d < 0) g.overdue.push({ ...t, daysUntil: d });
      else if (d === 0) g.dueToday.push({ ...t, daysUntil: 0 });
      else if (d <= 7) g.soon.push({ ...t, daysUntil: d });
      else g.later.push({ ...t, daysUntil: d });
    }
    return g;
  }, [tank.tasks]);

  const completeTask = async (taskId) => {
    setCompleting(taskId);
    setActionErr('');
    try {
      await api(`/api/tanks/${tank.id}/tasks/${taskId}/complete`, { method: 'POST' });
      setOpenId(null);
      await onReload();
    } catch (e) {
      setActionErr(e?.message || 'Could not mark that done. Try again.');
    } finally {
      setCompleting(null);
    }
  };
  // Delete sits a thumb's width from Edit; one stray tap used to remove a
  // task and its schedule with no way back.
  const removeTask = async (taskId) => {
    const task = (tank.tasks || []).find((t) => t.id === taskId);
    const ok = await confirm({
      title: task ? `Remove "${task.title}"?` : 'Remove this task?',
      body: 'Its schedule goes too. You can add it again from Tasks.',
      confirmLabel: 'Remove task',
      cancelLabel: 'Keep it',
      destructive: true,
    });
    if (!ok) return;
    setActionErr('');
    try {
      await api(`/api/tanks/${tank.id}/tasks/${taskId}`, { method: 'DELETE' });
      await onReload();
    } catch (e) {
      setActionErr(e?.message || 'Could not remove that task. Try again.');
    }
  };
  const saveEdit = async (taskId, patch) => {
    setActionErr('');
    try {
      await api(`/api/tanks/${tank.id}/tasks/${taskId}`, { method: 'PATCH', body: patch });
      setEditingId(null);
      await onReload();
    } catch (e) {
      setActionErr(e?.message || 'Could not save that task. Try again.');
    }
  };
  // "Keep it": start the schedule again from today rather than counting
  // how late it is.
  const restartTask = async (t) => {
    const every = Number(t.cadence_days) > 0 ? Number(t.cadence_days) : 7;
    await saveEdit(t.id, { due: new Date(Date.now() + every * 86400000).toISOString() });
  };

  const runAction = (explainer) => {
    if (explainer?.action === 'water_change') setWaterChange(true);
    else if (explainer?.action === 'icp') onOpenIcp?.();
    else if (explainer?.action === 'log') onOpenLog?.();
  };

  const renderRow = (t, label, color) => {
    const explainer = explainerForTank(matchTaskExplainer(t.title, explainers), tank);
    const open = openId === t.id;
    return (
      <div
        key={t.id}
        style={{
          borderRadius: 12,
          background: 'var(--surface)',
          border: `1px solid ${
            open
              ? 'var(--accent-border)'
              : label === 'Overdue'
                ? 'var(--alert-border)'
                : 'var(--surface-border)'
          }`,
        }}
      >
        <div style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* 44 px to tap, the 26 px ring drawn inside it. A viewer gets
              no ring: one that cannot be ticked reads as broken. */}
          {canEdit && (
            <button
              onClick={() => completeTask(t.id)}
              aria-label={`Complete ${t.title}`}
              style={{
                width: 44,
                height: 44,
                margin: -9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background: 'transparent',
                border: 'none',
                padding: 0,
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  border: `1.5px solid ${color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {completing === t.id && (
                  <svg width="12" height="12" viewBox="0 0 24 24" className="spin">
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      fill="none"
                      stroke={color}
                      strokeWidth="3"
                      strokeDasharray="14 28"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpenId(open ? null : t.id)}
            aria-expanded={open}
            style={{ flex: 1, minWidth: 0, textAlign: 'left', padding: 0 }}
          >
            <div style={{ fontSize: 15, color: 'var(--text)' }}>{t.title}</div>
            <div
              className="font-mono"
              style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}
            >
              {t.cadence_days ? `every ${t.cadence_days}d · ` : ''}
              {t.daysUntil == null
                ? ''
                : t.daysUntil < 0
                  ? `due ${-t.daysUntil}d ago`
                  : t.daysUntil === 0
                    ? 'due today'
                    : `in ${t.daysUntil}d`}
              {explainer || canEdit ? (
                <span style={{ color: 'var(--cyan)' }}>
                  {open ? ' · hide' : explainer ? ' · why it matters' : ' · change'}
                </span>
              ) : null}
            </div>
          </button>
        </div>
        {open && explainer && (
          <TaskWhy
            tank={tank}
            explainer={explainer}
            byParam={byParam}
            overdue={t.daysUntil != null && t.daysUntil < 0}
            onAction={() => runAction(explainer)}
            onDone={() => completeTask(t.id)}
            busy={completing === t.id}
            canEdit={canEdit}
          />
        )}
        {/* Edit and remove sit behind a tap on the task, one action per row
            (owner, 2026-10-21): a bin on every row was one wet-thumb tap from
            a deleted schedule. */}
        {open && canEdit && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="ts-chip"
              style={{ minHeight: 44, padding: '0 16px' }}
              onClick={() => setEditingId(t.id)}
            >
              Change it
            </button>
            <button
              type="button"
              className="ts-chip"
              style={{ minHeight: 44, padding: '0 16px' }}
              onClick={() => removeTask(t.id)}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (label, color, list) => {
    if (list.length === 0) return null;
    return (
      <section
        key={label}
        className="slide-in"
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
      >
        <GroupLabel label={label} color={color} count={list.length} />
        {list.map((t) =>
          editingId === t.id ? (
            <EditTaskRow
              key={t.id}
              task={t}
              onCancel={() => setEditingId(null)}
              onSave={(patch) => saveEdit(t.id, patch)}
            />
          ) : (
            renderRow(t, label, color)
          )
        )}
      </section>
    );
  };

  return (
    <div style={{ padding: '0 20px 40px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <h2 className="font-heading" style={{ fontSize: 15, letterSpacing: '0.08em' }}>
            MAINTENANCE
          </h2>
          <p
            className="font-heading"
            style={{
              fontSize: 12,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--cyan)',
              marginTop: 2,
            }}
          >
            Tap a task to see why it matters
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="font-heading"
            style={{
              fontSize: 12,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              padding: '6px 12px',
              minHeight: 44,
              borderRadius: 999,
              color: 'var(--cyan)',
              border: '1px solid var(--accent-border)',
            }}
          >
            {showAdd ? 'Close' : '+ Add'}
          </button>
        )}
      </div>
      {actionErr && (
        <div
          role="alert"
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            background: 'var(--alert-bg)',
            border: '1px solid var(--alert-border)',
            color: 'var(--alert)',
            fontSize: 15,
            lineHeight: 1.45,
          }}
        >
          {actionErr}
        </div>
      )}
      {canEdit && showAdd && (
        <AddTaskForm
          tank={tank}
          onDone={async () => {
            await onReload();
            setShowAdd(false);
          }}
        />
      )}
      {canEdit && <AtoOffer tank={tank} explainers={explainers} onReload={onReload} />}
      {groups.lapsed.length > 0 && (
        <section className="slide-in" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <GroupLabel
            label="Still doing these?"
            color="var(--amber, #f2c063)"
            count={groups.lapsed.length}
          />
          <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            These have been waiting a while, so reminders for them have stopped. Keep the ones that
            fit how you run the tank, change how often, or remove them.
          </div>
          {groups.lapsed.map((t) =>
            editingId === t.id ? (
              <EditTaskRow
                key={t.id}
                task={t}
                onCancel={() => setEditingId(null)}
                onSave={(patch) => saveEdit(t.id, patch)}
              />
            ) : (
              <LapsedRow
                key={t.id}
                task={t}
                canEdit={canEdit}
                onKeep={() => restartTask(t)}
                onChange={() => setEditingId(t.id)}
                onRemove={() => removeTask(t.id)}
              />
            )
          )}
        </section>
      )}
      {renderGroup('Overdue', 'var(--alert)', groups.overdue)}
      {renderGroup('Due today', 'var(--cyan)', groups.dueToday)}
      {renderGroup('Soon', 'var(--cyan-dim)', groups.soon)}
      {renderGroup('Later', 'var(--text-muted)', groups.later)}
      {(tank.tasks || []).length === 0 && (
        <div
          style={{
            padding: 20,
            textAlign: 'center',
            borderRadius: 12,
            background: 'var(--surface)',
            border: '1px solid var(--surface-border)',
            fontSize: 15,
            color: 'var(--text-muted)',
          }}
        >
          {canEdit ? 'No tasks yet — add your first above.' : 'No tasks on this tank yet.'}
        </div>
      )}
      {waterChange && (
        <WaterChangeSheet tank={tank} onClose={() => setWaterChange(false)} onDone={onReload} />
      )}
    </div>
  );
}

function GroupLabel({ label, color, count }) {
  return (
    <div
      className="font-heading"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color,
      }}
    >
      <span style={{ width: 4, height: 12, borderRadius: 2, background: color }} />
      {label} · {count}
    </div>
  );
}

const ACTION_LABEL = {
  log: 'Log a reading',
  icp: 'Upload an ICP result',
  water_change: 'Log the water change',
};

// What the tank's own numbers say about this task. Only fresh readings
// give a value: a stale one is not the tank's current state (the same rule
// the score and the morning email follow). For a test task, a stale or
// missing reading says when it was last measured instead.
function dataLines(tank, explainer, byParam) {
  const lines = [];
  for (const key of explainer.params || []) {
    const series = currentReadingsClient(byParam[key] || [], key);
    const latest = series[0];
    const p = effectiveParam(tank, key);
    // A card the tank hides is not shown here: a community tank's "Test
    // nitrate" listed "Phosphate: no reading yet" (feedback #74).
    if (p?.hidden) continue;
    const label = p?.label || key;
    if (!latest) {
      if (explainer.kind === 'test') lines.push(`${label}: no reading yet`);
      continue;
    }
    const fresh = assessFreshnessClient(series, key);
    const ago = daysSinceReading(latest.ts);
    const agoText =
      ago == null ? '' : ago === 0 ? 'today' : ago === 1 ? 'yesterday' : `${ago} days ago`;
    if (fresh?.reason === 'test_time_unknown') {
      if (explainer.kind === 'test') lines.push(`${label}: from the Trident, test time unknown`);
      continue;
    }
    if (fresh?.stale) {
      if (explainer.kind === 'test') lines.push(`${label}: last measured ${agoText}`);
      continue;
    }
    let shown;
    if (key === 'salinity') {
      const unit = readSalinityUnit();
      shown = `${formatSalinity(latest.value, unit)} ${salinityUnitLabel(unit)}`;
    } else {
      shown = `${Number(latest.value)}${p?.unit ? ` ${p.unit}` : ''}`;
    }
    const [lo, hi] = Array.isArray(p?.acceptable) ? p.acceptable : [];
    const v = Number(latest.value);
    const where =
      typeof lo === 'number' && typeof hi === 'number'
        ? v < lo
          ? 'below your range'
          : v > hi
            ? 'above your range'
            : 'in your range'
        : null;
    lines.push(`${label} ${shown}${where ? `, ${where}` : ''} (${agoText})`);
  }
  return lines;
}

function TaskWhy({ tank, explainer, byParam, overdue, onAction, onDone, busy, canEdit = true }) {
  const lines = dataLines(tank, explainer, byParam);
  const section = (title, body) => (
    <div style={{ marginTop: 10 }}>
      <div
        className="font-heading"
        style={{
          fontSize: 11,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: 15, color: 'var(--text)', lineHeight: 1.55, marginTop: 3 }}>
        {body}
      </div>
    </div>
  );
  // Overdue leads with what skipping costs; otherwise with why it matters.
  const why = section('Why it matters', explainer.why);
  const skip = section('If you skip it', explainer.skip);
  const btn = {
    padding: '9px 14px',
    borderRadius: 999,
    fontSize: 13,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontWeight: 600,
  };
  return (
    <div
      className="slide-in"
      style={{ padding: '0 14px 14px 48px', borderTop: '1px solid var(--surface-border)' }}
    >
      {lines.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {lines.map((l) => (
            <div key={l} className="font-mono" style={{ fontSize: 13, color: 'var(--cyan)' }}>
              {l}
            </div>
          ))}
        </div>
      )}
      {overdue ? (
        <>
          {skip}
          {why}
        </>
      ) : (
        <>
          {why}
          {skip}
        </>
      )}
      {Array.isArray(explainer.how) && explainer.how.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div
            className="font-heading"
            style={{
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--text-dim)',
            }}
          >
            How
          </div>
          <ol
            style={{
              margin: '4px 0 0',
              paddingLeft: 18,
              fontSize: 15,
              color: 'var(--text-muted)',
              lineHeight: 1.55,
            }}
          >
            {explainer.how.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ol>
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {explainer.action && ACTION_LABEL[explainer.action] && (
            <button
              type="button"
              onClick={onAction}
              className="font-heading"
              style={{ ...btn, background: 'var(--cyan)', color: 'var(--cyan-text)' }}
            >
              {ACTION_LABEL[explainer.action]}
            </button>
          )}
          <button
            type="button"
            onClick={onDone}
            disabled={busy}
            className="font-heading"
            style={{
              ...btn,
              color: 'var(--cyan)',
              border: '1px solid var(--accent-border)',
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? 'Saving…' : 'Mark done'}
          </button>
        </div>
      )}
    </div>
  );
}

function LapsedRow({ task, onKeep, onChange, onRemove, canEdit = true }) {
  const [busy, setBusy] = useState(false);
  const act = (fn) => async () => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };
  const btn = {
    padding: '7px 12px',
    borderRadius: 999,
    fontSize: 12,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    border: '1px solid var(--surface-border)',
    color: 'var(--text)',
    opacity: busy ? 0.5 : 1,
  };
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 12,
        background: 'var(--surface)',
        border: '1px solid var(--surface-border)',
      }}
    >
      <div style={{ fontSize: 15, color: 'var(--text)' }}>{task.title}</div>
      <div className="font-mono" style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>
        {lastDoneLabel(task)}
        {task.cadence_days ? ` · every ${task.cadence_days}d` : ''}
      </div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          <button
            type="button"
            disabled={busy}
            onClick={act(onKeep)}
            className="font-heading"
            style={{ ...btn, color: 'var(--cyan)', borderColor: 'var(--accent-border)' }}
          >
            Keep, start from today
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onChange}
            className="font-heading"
            style={btn}
          >
            Change how often
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={act(onRemove)}
            className="font-heading"
            style={{ ...btn, color: 'var(--text-muted)' }}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

// An ATO in the equipment list but no refill task: offer one. How often is
// the keeper's call, because evaporation varies too much for us to guess.
function AtoOffer({ tank, explainers, onReload }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(atoOfferKey(tank.id)) === '1';
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState(false);
  const hasAto = (tank.equipment || []).some((e) => e?.kind === 'ato');
  const hasTask = (tank.tasks || []).some(
    (t) => matchTaskExplainer(t.title, explainers)?.key === 'ato'
  );
  if (!hasAto || hasTask || dismissed) return null;
  const add = async (days) => {
    setBusy(true);
    try {
      await api(`/api/tanks/${tank.id}/tasks`, {
        method: 'POST',
        body: {
          title: 'Refill ATO reservoir',
          cadence_days: days,
          due: new Date(Date.now() + days * 86400000).toISOString(),
        },
      });
      await onReload();
    } finally {
      setBusy(false);
    }
  };
  const dismiss = () => {
    try {
      localStorage.setItem(atoOfferKey(tank.id), '1');
    } catch {
      // private mode: the offer just comes back next visit
    }
    setDismissed(true);
  };
  const chip = {
    padding: '7px 12px',
    borderRadius: 999,
    fontSize: 13,
    border: '1px solid var(--accent-border)',
    color: 'var(--cyan)',
    opacity: busy ? 0.5 : 1,
  };
  return (
    <div
      className="slide-in"
      style={{
        padding: 14,
        borderRadius: 12,
        background: 'var(--surface)',
        border: '1px solid var(--accent-border)',
      }}
    >
      <div style={{ fontSize: 15, color: 'var(--text)', fontWeight: 600 }}>
        Add a reminder to refill your ATO?
      </div>
      <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 4 }}>
        {isFreshwaterTank(tank)
          ? 'When the reservoir runs dry, the water level drops every day and the pump can run dry.'
          : 'When the reservoir runs dry, salinity creeps up every day and the pump can run dry.'}{' '}
        How often do you usually refill it?
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {ATO_CADENCES.map((d) => (
          <button key={d} type="button" disabled={busy} onClick={() => add(d)} style={chip}>
            Every {d} days
          </button>
        ))}
        <button
          type="button"
          onClick={dismiss}
          style={{ ...chip, color: 'var(--text-muted)', borderColor: 'var(--surface-border)' }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function AddTaskForm({ tank, onDone }) {
  const [title, setTitle] = useState('');
  const [cadence, setCadence] = useState('');
  const [dueInDays, setDueInDays] = useState('1');
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    const due = new Date(Date.now() + (Number(dueInDays) || 0) * 86400000).toISOString();
    try {
      await api(`/api/tanks/${tank.id}/tasks`, {
        method: 'POST',
        body: { title: title.trim(), cadence_days: cadence ? Number(cadence) : null, due },
      });
      onDone();
    } finally {
      setBusy(false);
    }
  };
  const box = {
    padding: 12,
    borderRadius: 12,
    background: 'var(--surface)',
    border: '1px solid var(--surface-border)',
  };

  return (
    <form
      onSubmit={save}
      className="slide-in"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div style={box}>
        <div
          className="font-heading"
          style={{
            fontSize: 11,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--text-dim)',
            marginBottom: 2,
          }}
        >
          Task
        </div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          placeholder="Filter sock swap"
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            padding: '4px 0',
            fontSize: 17,
            color: 'var(--text)',
          }}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={box}>
          <div
            className="font-heading"
            style={{
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--text-dim)',
              marginBottom: 2,
            }}
          >
            Repeat every
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <input
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              type="number"
              inputMode="numeric"
              placeholder="7"
              style={{
                width: 60,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: '4px 0',
                fontSize: 18,
                color: 'var(--text)',
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              }}
            />
            <span style={{ fontSize: 14, color: 'var(--text-dim)' }}>days</span>
          </div>
        </div>
        <div style={box}>
          <div
            className="font-heading"
            style={{
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--text-dim)',
              marginBottom: 2,
            }}
          >
            Due in
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <input
              value={dueInDays}
              onChange={(e) => setDueInDays(e.target.value)}
              type="number"
              inputMode="numeric"
              style={{
                width: 60,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: '4px 0',
                fontSize: 18,
                color: 'var(--text)',
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              }}
            />
            <span style={{ fontSize: 14, color: 'var(--text-dim)' }}>days</span>
          </div>
        </div>
      </div>
      <button
        type="submit"
        disabled={!title.trim() || busy}
        className="font-heading"
        style={{
          padding: '10px 0',
          borderRadius: 12,
          background: 'var(--cyan)',
          color: 'var(--cyan-text)',
          fontWeight: 600,
          fontSize: 13,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          opacity: !title.trim() || busy ? 0.5 : 1,
        }}
      >
        Add task
      </button>
    </form>
  );
}

function EditTaskRow({ task, onCancel, onSave }) {
  const [title, setTitle] = useState(task.title);
  const [cadence, setCadence] = useState(
    task.cadence_days != null ? String(task.cadence_days) : ''
  );
  const [dueDate, setDueDate] = useState(() => {
    if (!task.due) return '';
    const d = new Date(task.due);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      const patch = { title: title.trim() };
      patch.cadence_days = cadence === '' ? null : Number(cadence);
      if (dueDate) patch.due = new Date(dueDate + 'T12:00:00').toISOString();
      await onSave(patch);
    } finally {
      setBusy(false);
    }
  };

  const box = {
    padding: 10,
    borderRadius: 10,
    background: 'var(--blue-darker, #081436)',
    border: '1px solid var(--surface-border)',
  };
  const labelStyle = {
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--text-dim)',
    marginBottom: 2,
  };
  const inputStyle = {
    width: '100%',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: '4px 0',
    color: 'var(--text)',
    fontFamily: 'inherit',
  };

  return (
    <form
      onSubmit={submit}
      className="slide-in"
      style={{
        padding: 12,
        borderRadius: 12,
        background: 'var(--surface)',
        border: '1px solid var(--cyan)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={box}>
        <div className="font-heading" style={labelStyle}>
          Task
        </div>
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ ...inputStyle, fontSize: 17 }}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div style={box}>
          <div className="font-heading" style={labelStyle}>
            Repeat every
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <input
              value={cadence}
              onChange={(e) => setCadence(e.target.value)}
              type="number"
              inputMode="numeric"
              placeholder="—"
              style={{
                ...inputStyle,
                width: 60,
                fontSize: 18,
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              }}
            />
            <span style={{ fontSize: 14, color: 'var(--text-dim)' }}>days</span>
          </div>
        </div>
        <div style={box}>
          <div className="font-heading" style={labelStyle}>
            Due
          </div>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{
              ...inputStyle,
              fontSize: 15,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              colorScheme: 'dark',
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onCancel}
          className="font-heading"
          style={{
            flex: 1,
            padding: '10px 0',
            borderRadius: 12,
            color: 'var(--text-muted)',
            border: '1px solid var(--surface-border)',
            fontSize: 13,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!title.trim() || busy}
          className="font-heading"
          style={{
            flex: 1,
            padding: '10px 0',
            borderRadius: 12,
            background: 'var(--cyan)',
            color: 'var(--cyan-text)',
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            opacity: !title.trim() || busy ? 0.5 : 1,
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
